import { useTranslation, TFunction } from "react-i18next"
import { useForm, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
// hooks
import useExistedUser from "./hooks/useExistedUser"
import { useToggleVisibility } from "hooks/useToggleVisibility"
// components
import OnboardingLayout from "shared/components/OnboardingLayout"
import { TextField } from "shared/components/input"
import Button from "shared/components/Button"
// assets
import Onboarding from "assets/images/backgrounds/Onboarding1.svg"
// styles
import { SPrivateCourseColoredTitle, SRightText, SText, STitle } from "../styles"
import { Box, Container } from "@mui/material"
import Icon from "shared/components/Icon"

interface FormData {
  email: string
}

const validator = (t: TFunction) =>
  yup.object().shape({
    email: yup
      .string()
      .email(t("privateCourseSignup:inputFields:email:errors:format"))
      .required(t("privateCourseSignup:inputFields:email:errors:required")),
  })

interface PrivateCourseSignupProps {
  isModalView?: boolean
}

const PrivateCourseSignup = ({ isModalView = false }: PrivateCourseSignupProps) => {
  const { t } = useTranslation()
  const { isVisible: emailFocused, handleShow } = useToggleVisibility()

  const { isLoading, setEmail } = useExistedUser({ isModalView })

  const { handleSubmit, formState, control } = useForm<FormData>({
    resolver: yupResolver(validator(t)),
    defaultValues: {
      email: "",
    },
    reValidateMode: "onChange",
  })

  const handleFormSubmit = ({ email }: FormData) => {
    setEmail(email)
  }

  const privateCourseSignupSideComponent = () => (
    <Box
      sx={[
        ({ breakpoints }) => ({
          display: "flex",
          flexDirection: "column",
          gap: 8,
          [breakpoints.down("sm")]: {
            gap: 4,
          },
        }),
      ]}
    >
      <STitle
        sx={[
          (theme) => ({
            [theme.breakpoints.down("xs")]: {
              fontSize: "22px",
              fontWeight: "400",
            },
            [theme.breakpoints.down("xxxs")]: {
              marginBottom: "0px !important",
            },
          }),
        ]}
      >
        {t("privateCourseSignup:infoTitleOne")}
        <SPrivateCourseColoredTitle>{t("privateCourseSignup:infoTitleTwo")}</SPrivateCourseColoredTitle>
      </STitle>
      <Box
        sx={[
          ({ breakpoints }) => ({
            display: "flex",
            flexDirection: "column",
            gap: 8,
            [breakpoints.down("sm")]: {
              gap: 6,
            },
            [breakpoints.down("xxxs")]: {
              gap: 4,
            },
          }),
        ]}
      >
        <Container sx={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Icon
            name="updates"
            sx={[
              (theme) => ({
                display: "flex",
                width: "26px",
                height: "26px",
                [theme.breakpoints.down("sm")]: {
                  width: "22px",
                  height: "22px",
                },
              }),
            ]}
          />
          <SRightText
            variant="h3"
            sx={[
              (theme) => ({
                [theme.breakpoints.down("sm")]: {
                  fontSize: "14px",
                },
              }),
            ]}
          >
            {t("privateCourseSignup:updatesText")}
          </SRightText>
        </Container>
        <Container sx={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Icon
            name="clock"
            sx={[
              (theme) => ({
                display: "flex",
                width: "26px",
                height: "26px",
                [theme.breakpoints.down("sm")]: {
                  width: "22px",
                  height: "22px",
                },
              }),
            ]}
          />
          <SRightText
            variant="h3"
            sx={[
              (theme) => ({
                [theme.breakpoints.down("sm")]: {
                  fontSize: "14px",
                },
              }),
            ]}
          >
            {t("privateCourseSignup:timeSavingText")}
          </SRightText>
        </Container>
        <Container sx={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Icon
            name="doctor"
            sx={[
              (theme) => ({
                display: "flex",
                width: "26px",
                height: "26px",
                [theme.breakpoints.down("sm")]: {
                  width: "22px",
                  height: "22px",
                },
              }),
            ]}
          />
          <SRightText
            variant="h3"
            sx={[
              (theme) => ({
                [theme.breakpoints.down("sm")]: {
                  fontSize: "14px",
                },
              }),
            ]}
          >
            {t("privateCourseSignup:expertsText")}
          </SRightText>
        </Container>
        <Container sx={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Icon
            name="certified"
            sx={[
              (theme) => ({
                display: "flex",
                width: "26px",
                height: "26px",
                [theme.breakpoints.down("sm")]: {
                  width: "22px",
                  height: "22px",
                },
              }),
            ]}
          />
          <SRightText
            variant="h3"
            sx={[
              (theme) => ({
                [theme.breakpoints.down("sm")]: {
                  fontSize: "14px",
                },
              }),
            ]}
          >
            {t("privateCourseSignup:qualityText")}
          </SRightText>
        </Container>
      </Box>
    </Box>
  )

  return (
    <OnboardingLayout
      focused={emailFocused}
      rightBackgroundSrc={Onboarding}
      isModalView={isModalView}
      showBackBtn={isModalView}
      PrivateCourseSignupComponent={privateCourseSignupSideComponent()}
      sxLeftSide={[
        (theme) => ({
          [theme.breakpoints.down("sm")]: {
            flexGrow: "initial",
          },
        }),
      ]}
      sxRightSide={[
        (theme) => ({
          [theme.breakpoints.down("sm")]: {
            padding: "36px 40px",
          },
        }),
      ]}
    >
      <STitle>{t("privateCourseSignup:title")}</STitle>
      <SText variant="h5">{t("privateCourseSignup:text")}</SText>
      <form
        style={{ height: "100%", display: "flex", flexDirection: "column" }}
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <Controller
          name="email"
          control={control}
          render={({
            field,
            formState: {
              errors: { email },
            },
          }) => (
            <TextField
              type="email"
              onFocus={handleShow}
              inputProps={field}
              error={!!email?.message}
              helperText={email?.message}
              sx={{ mb: 6 }}
              placeholder={t("privateCourseSignup:inputFields:email:placeholder")}
            />
          )}
        />

        <Button
          sx={{ mt: "auto" }}
          type="submit"
          disabled={!!Object.keys(formState.errors).length || isLoading}
          color="secondary"
          fullWidth
          variant="contained"
        >
          {t("privateCourseSignup:submitButtonText")}
        </Button>
      </form>
    </OnboardingLayout>
  )
}

export default PrivateCourseSignup
